package com.speedsoftware.nibbler.ui.theme

import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Shapes
import androidx.compose.material3.Typography
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

enum class ThemeMode { SYSTEM, DARK, LIGHT }

/** The user's theme choice. Colours read it, so changing [mode] recolours the whole UI. */
object ThemeSettings {
    var mode by mutableStateOf(
        com.speedsoftware.nibbler.loadStoredThemeMode()?.let { stored -> ThemeMode.values().firstOrNull { it.name == stored } } ?: ThemeMode.SYSTEM
    )
        private set

    val isDark: Boolean
        get() = when (mode) {
            ThemeMode.DARK -> true
            ThemeMode.LIGHT -> false
            ThemeMode.SYSTEM -> systemDark
        }

    private val systemDark: Boolean = com.speedsoftware.nibbler.systemPrefersDarkTheme()

    fun select(newMode: ThemeMode) {
        mode = newMode
        com.speedsoftware.nibbler.storeThemeMode(newMode.name)
    }
}

private class Palette(
    val accent: Color, val accentSoft: Color, val accentStrong: Color,
    val success: Color, val warning: Color, val danger: Color, val info: Color,
    val background: Color, val deep: Color, val lowest: Color, val low: Color, val container: Color,
    val high: Color, val highest: Color,
    val onSurface: Color, val onSurfaceVariant: Color, val outline: Color,
    val secondaryContainer: Color, val onSecondaryContainer: Color,
    val errorContainer: Color, val onError: Color,
    val tertiaryFixed: Color, val onTertiary: Color
)

private val DarkPalette = Palette(
    accent = Color(0xFF8B7CF6), accentSoft = Color(0xFF2A2547), accentStrong = Color(0xFF6D5DE8),
    success = Color(0xFF34D399), warning = Color(0xFFF5B544), danger = Color(0xFFF87171), info = Color(0xFF7AA7F7),
    background = Color(0xFF111116), deep = Color(0xFF0D0D11), lowest = Color(0xFF14141A), low = Color(0xFF18181F),
    container = Color(0xFF1C1C24), high = Color(0xFF23232D), highest = Color(0xFF2B2B36),
    onSurface = Color(0xFFECECF1), onSurfaceVariant = Color(0xFF9C9CAE), outline = Color(0xFF2C2C37),
    secondaryContainer = Color(0xFF27304A), onSecondaryContainer = Color(0xFFB9CCF5),
    errorContainer = Color(0xFF3F1D22), onError = Color(0xFF3A0A0E),
    tertiaryFixed = Color(0xFFC9C1FB), onTertiary = Color(0xFF14102B)
)

private val LightPalette = Palette(
    accent = Color(0xFF6D5DE8), accentSoft = Color(0xFFECE9FD), accentStrong = Color(0xFF5B4BD6),
    success = Color(0xFF059669), warning = Color(0xFFD97706), danger = Color(0xFFDC2626), info = Color(0xFF2563EB),
    background = Color(0xFFF6F6FA), deep = Color(0xFFFFFFFF), lowest = Color(0xFFEFEFF5), low = Color(0xFFF3F3F8),
    container = Color(0xFFFFFFFF), high = Color(0xFFFFFFFF), highest = Color(0xFFE6E6EF),
    onSurface = Color(0xFF1B1B22), onSurfaceVariant = Color(0xFF5D5D6E), outline = Color(0xFFDCDCE6),
    secondaryContainer = Color(0xFFE3ECFD), onSecondaryContainer = Color(0xFF1E3A8A),
    errorContainer = Color(0xFFFDE8E8), onError = Color(0xFF7F1D1D),
    tertiaryFixed = Color(0xFF4F46C8), onTertiary = Color(0xFFFFFFFF)
)

/**
 * Colour tokens. Every value follows the current [ThemeSettings] (dark or light), so composables that read
 * them recolour automatically. The app moved from a Bauhaus look to a calmer neutral design with one violet
 * accent; token names that still say "Bauhaus" or "Dark" are kept so existing screens keep compiling, but
 * their values now follow the theme and the new palette.
 */
object NibblerColors {
    private val p: Palette get() = if (ThemeSettings.isDark) DarkPalette else LightPalette

    // Accent
    val Accent: Color get() = p.accent
    val AccentSoft: Color get() = p.accentSoft
    val AccentStrong: Color get() = p.accentStrong
    val OnAccent: Color get() = Color.White

    // Status colours
    val Success: Color get() = p.success
    val Warning: Color get() = p.warning
    val Danger: Color get() = p.danger
    val Info: Color get() = p.info

    // Legacy names
    val BauhausRed: Color get() = p.danger
    val BauhausCoral: Color get() = p.accent
    val BauhausYellow: Color get() = p.accent
    val BauhausYellowBright: Color get() = p.warning
    val BauhausBlue: Color get() = if (ThemeSettings.isDark) Color(0xFF3B4A7A) else Color(0xFF8FA6E0)
    val BauhausBlueLight: Color get() = p.info
    val ElectricIndigo: Color get() = p.accent

    // Surfaces (the "Dark" names are legacy: they follow the theme)
    val DarkBackground: Color get() = p.background
    val DarkSurfaceDeep: Color get() = p.deep
    val DarkSurfaceContainerLowest: Color get() = p.lowest
    val DarkSurfaceContainerLow: Color get() = p.low
    val DarkSurfaceContainer: Color get() = p.container
    val DarkSurfaceContainerHigh: Color get() = p.high
    val DarkSurfaceContainerHighest: Color get() = p.highest
    val DarkOnSurface: Color get() = p.onSurface
    val DarkOnSurfaceVariant: Color get() = p.onSurfaceVariant
    val DarkOutline: Color get() = p.onSurface
    val DarkOutlineThin: Color get() = p.outline
    val DarkOutlineVariant: Color get() = p.outline

    val Primary: Color get() = p.accent
    val PrimaryContainer: Color get() = p.accentStrong
    val OnPrimary: Color get() = Color.White
    val OnPrimaryContainer: Color get() = Color.White

    val Secondary: Color get() = p.info
    val SecondaryContainer: Color get() = p.secondaryContainer
    val OnSecondaryContainer: Color get() = p.onSecondaryContainer

    val Tertiary: Color get() = p.accent
    val TertiaryFixed: Color get() = p.tertiaryFixed
    val OnTertiary: Color get() = p.onTertiary

    val Background: Color get() = p.background
    val SurfaceDeep: Color get() = p.deep
    val SurfaceContainerLowest: Color get() = p.lowest
    val SurfaceContainerLow: Color get() = p.low
    val SurfaceContainer: Color get() = p.container
    val SurfaceContainerHigh: Color get() = p.high
    val SurfaceContainerHighest: Color get() = p.highest

    val OnSurface: Color get() = p.onSurface
    val OnSurfaceVariant: Color get() = p.onSurfaceVariant
    val Outline: Color get() = p.onSurface
    val OutlineVariant: Color get() = p.outline

    val Error: Color get() = p.danger
    val ErrorContainer: Color get() = p.errorContainer
    val OnError: Color get() = p.onError

    // Light-palette names some code still references
    val LightBackground: Color get() = LightPalette.background
    val LightSurfaceContainer: Color get() = LightPalette.highest
    val LightSurfaceContainerLowest: Color get() = LightPalette.container
    val LightOnSurface: Color get() = LightPalette.onSurface
    val LightOnSurfaceVariant: Color get() = LightPalette.onSurfaceVariant
    val LightOutline: Color get() = LightPalette.onSurface
    val LightOutlineVariant: Color get() = LightPalette.outline

    // Tag chips
    val TagBackendBg: Color get() = p.danger.copy(alpha = 0.16f)
    val TagBackendFg: Color get() = if (ThemeSettings.isDark) Color(0xFFFFB4B4) else Color(0xFF991B1B)
    val TagSecurityBg: Color get() = p.warning.copy(alpha = 0.16f)
    val TagSecurityFg: Color get() = p.warning
    val TagDesignBg: Color get() = p.info.copy(alpha = 0.16f)
    val TagDesignFg: Color get() = p.onSecondaryContainer
    val TagFrontendBg: Color get() = p.accent.copy(alpha = 0.18f)
    val TagFrontendFg: Color get() = p.tertiaryFixed
    val TagDevOpsBg: Color get() = p.highest
    val TagDevOpsFg: Color get() = p.onSurfaceVariant
}

private fun materialScheme(dark: Boolean) = if (dark) {
    darkColorScheme(
        primary = NibblerColors.Primary, onPrimary = NibblerColors.OnPrimary,
        primaryContainer = NibblerColors.PrimaryContainer, onPrimaryContainer = NibblerColors.OnPrimaryContainer,
        secondary = NibblerColors.Secondary, onSecondary = Color(0xFF14213D),
        secondaryContainer = NibblerColors.SecondaryContainer, onSecondaryContainer = NibblerColors.OnSecondaryContainer,
        tertiary = NibblerColors.Tertiary, onTertiary = NibblerColors.OnTertiary,
        background = NibblerColors.Background, onBackground = NibblerColors.OnSurface,
        surface = NibblerColors.SurfaceContainer, onSurface = NibblerColors.OnSurface,
        surfaceVariant = NibblerColors.SurfaceContainerHigh, onSurfaceVariant = NibblerColors.OnSurfaceVariant,
        outline = NibblerColors.OutlineVariant, error = NibblerColors.Error, onError = NibblerColors.OnError
    )
} else {
    lightColorScheme(
        primary = NibblerColors.Primary, onPrimary = NibblerColors.OnPrimary,
        primaryContainer = NibblerColors.PrimaryContainer, onPrimaryContainer = NibblerColors.OnPrimaryContainer,
        secondary = NibblerColors.Secondary, onSecondary = Color.White,
        secondaryContainer = NibblerColors.SecondaryContainer, onSecondaryContainer = NibblerColors.OnSecondaryContainer,
        tertiary = NibblerColors.Tertiary, onTertiary = NibblerColors.OnTertiary,
        background = NibblerColors.Background, onBackground = NibblerColors.OnSurface,
        surface = NibblerColors.SurfaceContainer, onSurface = NibblerColors.OnSurface,
        surfaceVariant = NibblerColors.SurfaceContainerHighest, onSurfaceVariant = NibblerColors.OnSurfaceVariant,
        outline = NibblerColors.OutlineVariant, error = NibblerColors.Error, onError = Color.White
    )
}

// Softly rounded corners everywhere
val NibblerShapes = Shapes(
    extraSmall = RoundedCornerShape(8.dp),
    small = RoundedCornerShape(6.dp),
    medium = RoundedCornerShape(8.dp),
    large = RoundedCornerShape(12.dp),
    extraLarge = RoundedCornerShape(16.dp)
)

val NibblerTypography = Typography(
    displayLarge = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Bold,
        fontSize = 40.sp,
        lineHeight = 44.sp,
        letterSpacing = (-0.03).sp
    ),
    headlineLarge = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Bold,
        fontSize = 28.sp,
        lineHeight = 34.sp,
        letterSpacing = (-0.02).sp
    ),
    headlineMedium = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Bold,
        fontSize = 22.sp,
        lineHeight = 28.sp,
        letterSpacing = (-0.01).sp
    ),
    headlineSmall = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.SemiBold,
        fontSize = 16.sp,
        lineHeight = 22.sp
    ),
    bodyLarge = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Normal,
        fontSize = 16.sp,
        lineHeight = 24.sp
    ),
    bodyMedium = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Normal,
        fontSize = 14.sp,
        lineHeight = 20.sp
    ),
    bodySmall = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Normal,
        fontSize = 12.sp,
        lineHeight = 16.sp
    ),
    labelLarge = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.SemiBold,
        fontSize = 13.sp,
        lineHeight = 16.sp,
        letterSpacing = 0.sp
    ),
    labelMedium = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Medium,
        fontSize = 12.sp,
        lineHeight = 15.sp,
        letterSpacing = 0.sp
    ),
    labelSmall = TextStyle(
        fontFamily = FontFamily.SansSerif,
        fontWeight = FontWeight.Medium,
        fontSize = 10.sp,
        lineHeight = 13.sp,
        letterSpacing = 0.sp
    )
)

@Composable
fun NibblerTheme(
    darkTheme: Boolean = ThemeSettings.isDark,
    content: @Composable () -> Unit
) {
    MaterialTheme(
        colorScheme = materialScheme(darkTheme),
        shapes = NibblerShapes,
        typography = NibblerTypography,
        content = content
    )
}
